import { Component } from '@opten/compiler/metadata/component';
import { OnInit } from '@opten/compiler/metadata/lifecycle';
import { hasClass } from '@opten/dom/has-class';
import { addClass } from '@opten/dom/add-class';
import { removeClass } from '@opten/dom/remove-class';
import { getSelectorElementFromElement } from '@opten/dom/get-selector-element-from-element';
import { Collapse } from '@opten/collapse/initializer';
import { changedSizeBetweenMobileAndDesktop, isPad } from '../../utilities/detection';
import { lockScrollbar, unlockScrollbar } from '../../utilities/scroll-helper';

// todo: @opten/typescrpit?
@Component({ className: 'js-navbar' })
export class Navbar implements OnInit {
    private _backdropElement: HTMLElement;

    _header: HTMLElement;

    private _navbarTogglerElements: Array<HTMLElement>;
    private _navbar1stLevelElements: Array<HTMLElement>;
    private _navbar2ndLevelElements: Array<HTMLElement>;
    private _navbarHeaderElements: Array<HTMLElement>;

    private _mainScrollElement: HTMLElement;
    private _scrollableElements: Array<HTMLElement>;
    private _togglerElements: Array<HTMLElement>;

    private _isOpen: boolean = false;
    navbarElement: HTMLElement;
    private _currentWindowWidth: number;


    constructor(private element: HTMLElement) {
        this.navbarElement = element;
        this._backdropElement = document.getElementsByClassName(
            'js-navbar-backdrop'
        )[0] as HTMLElement;

        // todo: @ComponentRef()?
        this._header = document.getElementsByClassName(
            'js-header'
        )[0] as HTMLElement;

        this._navbar1stLevelElements = Array.from(
            document.querySelectorAll(`[id^="c-navbar-nav-true"]`)
        );

        this._navbar2ndLevelElements = Array.from(
            document.querySelectorAll(`[id^="c-navbar-collapse-"]`)
        );

        // not navbar because we could also have some in the header
        this._navbarTogglerElements = Array.from(
            this._header.querySelectorAll(`[data-toggle='navbar']`)
        );

        this._navbarHeaderElements = Array.from(
            document.getElementsByClassName('js-navbar-header')
        ) as Array<HTMLElement>;

        this._mainScrollElement = Array.from(element.children).filter(o =>
            hasClass(o, 'c-navbar__scrollable')
        )[0] as HTMLElement;

        this._scrollableElements = Array.from(
            this.element.getElementsByClassName('c-navbar__scrollable')
        ) as Array<HTMLElement>;

        this._togglerElements = Array.from(
            document.getElementsByClassName('js-navbar-toggler')
        ) as Array<HTMLElement>;
    }

    onInit() {
        this._navbarTogglerElements.forEach(toggler => {
            toggler.addEventListener('click', event => {
                event.preventDefault();
                if (hasClass(document.body, 'modal--is-open') === false) {
                    const target = getSelectorElementFromElement(toggler);

                    if (target) {
                        this._click(target);
                    }
                }
            });
        });

        this._backdropElement.addEventListener('click', () => this.close());

        this._handleResize(true);
        window.addEventListener('resize', () => this._handleResize());
    }

    private _handleNavbarHeaderClick = (event: MouseEvent) => {

        event.preventDefault();
        addClass((event.target as Element).parentElement, '--collapsed');

        this._scrollableElements.forEach(scrollable =>
            this._removeScrollable(scrollable)
        );
        this._setScrollable(this._mainScrollElement);
    }

    private _addMobileListeners() {
        if (isPad()) {
            this._navbarHeaderElements.forEach(header =>
                header.addEventListener('click', this._handleNavbarHeaderClick)
            );
        }
    }

    private _removeMobileListeners() {
        if (!isPad()) {
            this._navbarHeaderElements.forEach(header =>
                header.removeEventListener(
                    'click',
                    this._handleNavbarHeaderClick
                )
            );
        }
    }

    private _handleResize(isInitialLoad = false) {
        const oldWindowWidth = this._currentWindowWidth ? parseInt(this._currentWindowWidth.toString(), 10) : window.innerWidth;
        const resizedBetweenMobileAndDesktop = changedSizeBetweenMobileAndDesktop(oldWindowWidth);
        this._currentWindowWidth = window.innerWidth;

        if (resizedBetweenMobileAndDesktop || isInitialLoad) {
            if(isInitialLoad === false){
                unlockScrollbar(document.body, true);
            }
            this.close();            

            if (isPad()) {
                this._addMobileListeners();

                const height = Math.round(
                    this._header.getBoundingClientRect().height
                );

                // make the headers same height as the header itself
                Array.from(
                    this.element.getElementsByClassName('c-navbar__header')
                ).forEach(header => {
                    (header as HTMLElement).style.height = `${height}px`;
                });

                this._navbar1stLevelElements.forEach(navbar => {
                    const isOpen =
                        hasClass(
                            document.querySelector(`a[href='${navbar.id}']`),
                            'is--active'
                        ) || navbar.getElementsByClassName('is--active').length;
                    const collapseInstance = Collapse.getInstance(navbar as HTMLElement);
                    if (isOpen) {
                        if (hasClass(navbar, 'collapse--show') == false) {
                            collapseInstance.show();

                        }
                    } else {
                        collapseInstance.hide();
                    }
                });

                this._navbar2ndLevelElements.forEach(element =>
                    Collapse.getInstance(element as HTMLElement).destroy()
                );
            } else {
                this._removeMobileListeners();

                // reset height, because we don't need it
                this._scrollableElements.forEach(
                    scrollable => (scrollable.style.height = null)
                );

                this._navbar1stLevelElements.forEach(navbar =>
                    Collapse.getInstance(navbar as HTMLElement).destroy()
                );

                this._navbar2ndLevelElements.forEach(element =>
                    this._collapse(
                        element,
                        element.getElementsByClassName('is-active').length > 0
                    )
                );
            }
        }
    }

    public toggle() {
        this._isOpen ? this.close() : this.open();
    }

    public open() {
        if (this._isOpen) {
            return;
        }

        // mostly for iOS devices
        lockScrollbar(() => {
            removeClass(this.element, '--collapsed');

            // set the width to the navbar-toggler
            const left = Math.round(
                this._togglerElements[0].getBoundingClientRect().left
            );
            /*this.element.style.width = `${left}px`;*/

            addClass(this._backdropElement, '--show');

            this._togglerElements.forEach(toggler =>
                addClass(toggler, '--expanded')
            );

            // check if we're on a page (a container already has --scroll)
            // so we don't apply the scroll class otherwise we might cut off
            // a sub-navigation.
            const hasScroll = this._scrollableElements.filter(o =>
                hasClass(o, '--scroll')
            );

            if (!hasScroll.length) {
                this._setScrollable(this._mainScrollElement);
            }
            addClass(this._header, '--expanded');
            this._isOpen = true;
        });
    }

    public close() {
        if (!this._isOpen) {
            return;
        }

        addClass(this.element, '--collapsed');
        this.element.style.width = null; // reset to default
        removeClass(this._backdropElement, '--show');

        this._togglerElements.forEach(toggler =>
            removeClass(toggler, '--expanded')
        );

        // mostly for iOS devices
        unlockScrollbar();

        if (!isPad()) {
            this._navbar1stLevelElements.forEach(navbar =>
                Collapse.getInstance(navbar as HTMLElement).hide()
            );
        }
        removeClass(this._header, '--expanded');
        this._isOpen = false;
    }

    private _click(target: HTMLElement) {
        if (isPad()) {
            if (!this._isOpen) {

                // todo: better way? if we have a slower animation you see the other closing...
                // maybe we have some --collapsed in the cshtml
                // because we're on a certain page
                // so close all of them...
                this._navbar2ndLevelElements.forEach(navbar =>
                    addClass(navbar.firstElementChild, '--collapsed')
                );

                this.open();
            }

            if (hasClass(target, '--collapsed')) {
                removeClass(target, '--collapsed');

                const scrollable = target.getElementsByClassName(
                    'c-navbar__scrollable'
                )[0] as HTMLElement;

                this._scrollableElements.forEach(o =>
                    this._removeScrollable(o)
                );
                this._setScrollable(scrollable);
            }
        } else {
            // todo: better set data-toggle='navbar' to 'collapse' if desktop?
            // todo: closest?
            const collapseElement = target.parentElement;
            const isOpen = Collapse.getInstance(collapseElement as HTMLElement).isOpen();

            this._collapse(collapseElement, !isOpen);

            // close others because we cannot use data-parent
            // otherwise the mobile will be strange...
            this._navbar2ndLevelElements
                .filter(t => t !== collapseElement)
                .forEach(target => this._collapse(target, false));
        }
    }

    private _collapse(target: HTMLElement, show: boolean) {
        const navbar = target.getElementsByClassName('c-navbar')[0];

        const toggler = document.querySelector(
            `a[href='#${navbar.id}']`
        ) as HTMLElement;

        if (show) {
            Collapse.getInstance(target as HTMLElement).show();
        } else {
            Collapse.getInstance(target as HTMLElement).hide();
        }

        this._updateAria(toggler, show);
    }

    private _setScrollable(element: HTMLElement) {
        let height = Math.round(this._header.getBoundingClientRect().height);

        const titleElement = Array.from(element.parentElement.children).filter(
            o => hasClass(o, 'c-navbar__title')
        )[0];

        if (titleElement) {
            height += Math.round(titleElement.getBoundingClientRect().height);
        }

        element.style.height = `calc(100% - ${height}px)`;

        removeClass(element, '--lock');
        addClass(element, '--scroll');
    }

    private _removeScrollable(element: HTMLElement) {
        element.style.height = null;

        addClass(element, '--lock');
        removeClass(element, '--scroll');
    }

    private _updateAria(toggler: HTMLElement, isOpen: boolean) {
        toggler.setAttribute('aria-expanded', isOpen.toString().toLowerCase());
    }
}
